<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import SyntaxHighlightedInput from '@/components/Query/SyntaxHighlightedInput.vue'
import TimeRangeDropdown from './TimeRangeDropdown.vue'
import SourceLogSelector from './SourceLogSelector.vue'
import { useDataSourcesStore } from '@/stores/dataSources'

const dataSourcesStore = useDataSourcesStore()

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  loading: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  hasQueryChanges: {
    type: Boolean,
    default: false
  },
  timeRange: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'search', 'reset-query', 'time-range-change'])

// Expand/collapse state
const expanded = ref(false)
const searchBarRef = ref(null)

function toggleExpanded() {
  expanded.value = !expanded.value
}

function collapse() {
  expanded.value = false
}

function handleClickOutside(event) {
  if (searchBarRef.value && !searchBarRef.value.contains(event.target)) {
    collapse()
  }
}

// Collapse when search starts
watch(() => props.loading, (newLoading, oldLoading) => {
  if (newLoading && !oldLoading) {
    collapse()
  }
})

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

function handleResetQuery() {
  emit('reset-query')
}

const searchDisabled = computed(() =>
  props.loading || props.disabled || !props.modelValue.trim() || dataSourcesStore.hasNoSelection
)

function handleSubmit() {
  if (!searchDisabled.value) {
    emit('search')
  }
}
</script>

<template>
  <div class="event-search-bar" ref="searchBarRef">
    <form @submit.prevent="handleSubmit" class="search-form" :class="{ expanded }">
      <SourceLogSelector :disabled="loading || disabled" />

      <div class="search-input-wrapper">
        <SyntaxHighlightedInput
          :modelValue="modelValue"
          @update:modelValue="$emit('update:modelValue', $event)"
          placeholder="Search events..."
          :disabled="loading || disabled"
          :multiline="expanded"
          @submit="handleSubmit"
          class="search-input"
        />
      </div>

      <div class="search-bar-actions">
        <!-- Expand/Collapse Button -->
        <button
          type="button"
          class="expand-btn"
          @click.stop="toggleExpanded"
          :title="expanded ? 'Collapse to single line' : 'Expand to multi-line'"
        >
          <font-awesome-icon :icon="['fas', expanded ? 'chevron-up' : 'chevron-down']" />
        </button>

        <!-- Reset Query Button (only visible when input differs from last executed) -->
        <button
          v-if="hasQueryChanges"
          type="button"
          class="reset-query-btn"
          @click="handleResetQuery"
          title="Reset to last executed query"
        >
          <font-awesome-icon :icon="['fas', 'rotate-left']" />
        </button>

        <!-- Time Range Dropdown -->
        <TimeRangeDropdown
          :modelValue="timeRange"
          @update:modelValue="$emit('time-range-change', $event)"
          :disabled="loading || disabled"
        />

        <!-- Search Button -->
        <button
          type="submit"
          class="action-btn search-btn"
          :disabled="searchDisabled"
        >
          <font-awesome-icon v-if="!loading" :icon="['fas', 'magnifying-glass']" />
          <font-awesome-icon v-else :icon="['fas', 'spinner']" spin />
        </button>
      </div>
    </form>
  </div>
</template>

<style scoped>
.event-search-bar {
  padding-bottom: 0;
  padding-top: 0;
  min-height: calc(3.5rem + 2px);
  overflow: visible;
}

.search-form {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding-left: 1rem;
  background: #151a22;
  border: 1px solid #2a3441;
  border-radius: 0.75rem;
  transition: all 0.2s;
}

.search-form.expanded {
  position: relative;
  height: 400px;
  z-index: 100;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  align-items: flex-start;
}

.search-form:focus-within {
  border-color: #7357D2;
}

.search-input-wrapper {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.search-form.expanded .search-input-wrapper {
  height: 100%;
  padding: 0.5rem 0;
}

.search-input {
  width: 100%;
}

.search-form.expanded .search-input {
  height: 100%;
}

.search-bar-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-right: 0.75rem;
  flex-shrink: 0;
}

.search-form.expanded :deep(.source-log-selector),
.search-form.expanded .search-bar-actions {
  margin-top: 0.75rem;
}

.action-btn {
  background: rgba(115, 87, 210, 0.2);
  border: 1px solid rgba(115, 87, 210, 0.4);
  border-radius: 0.375rem;
  padding: 0.4rem 0.75rem;
  height: 32px;
  color: #E5E7EB;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.action-btn:hover:not(:disabled) {
  background: rgba(115, 87, 210, 0.35);
  border-color: #7357D2;
  color: #fff;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.reset-query-btn {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.375rem;
  padding: 0.4rem 0.5rem;
  height: 32px;
  color: #6B7280;
  font-size: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.reset-query-btn:hover {
  color: #E5E7EB;
  border-color: rgba(115, 87, 210, 0.4);
  background: rgba(115, 87, 210, 0.1);
}

.expand-btn {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.375rem;
  padding: 0.4rem 0.5rem;
  height: 32px;
  color: #6B7280;
  font-size: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.expand-btn:hover {
  color: #E5E7EB;
  border-color: rgba(115, 87, 210, 0.4);
  background: rgba(115, 87, 210, 0.1);
}
</style>
